<div class="space-y-5">

  <!-- Header -->
  <div class="flex items-center justify-between">
    <div>
      <h2 class="text-2xl font-bold text-gray-800">Activation Codes</h2>
      <p class="text-sm text-gray-500 mt-1">
        {{ filteredCodes.length }} code{{ filteredCodes.length === 1 ? '' : 's' }} found
      </p>
    </div>
    <button
      (click)="openGenerateModal()"
      class="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
      </svg>
      Generate Codes
    </button>
  </div>

  <!-- Search -->
  <div class="relative">
    <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
    </svg>
    <input
      type="text"
      [(ngModel)]="searchQuery"
      (ngModelChange)="onSearch()"
      placeholder="Search by code, role, form or status..."
      class="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-primary transition-colors"
    />
  </div>

  <!-- Bulk action bar -->
  <div *ngIf="selectedCount > 0" class="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-primary/5 border border-primary/20">
    <div class="flex items-center gap-2">
      <svg class="w-4 h-4 text-primary" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
      </svg>
      <span class="text-sm font-semibold text-primary">{{ selectedCount }} code{{ selectedCount === 1 ? '' : 's' }} selected</span>
    </div>
    <div class="flex items-center gap-2">
      <button
        (click)="clearSelection()"
        class="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        Clear
      </button>
      <button
        (click)="openBulkRevokeModal()"
        class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold cursor-pointer hover:opacity-90 transition-opacity"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"/>
        </svg>
        Revoke Selected
      </button>
    </div>
  </div>

  <!-- Loader -->
  <app-loader-component [loading]="loading" message="Loading activation codes..."></app-loader-component>

  <!-- Error -->
  <div *ngIf="errorMsg && !loading" class="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600">
    <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/>
    </svg>
    {{ errorMsg }}
  </div>

  <!-- Empty -->
  <div *ngIf="!loading && !errorMsg && filteredCodes.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
    <svg class="w-10 h-10 text-gray-300 mb-3" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 0 1 21.75 8.25Z"/>
    </svg>
    <p class="text-sm font-medium text-gray-600">No activation codes found</p>
    <p class="text-xs text-gray-400 mt-1">Generate a batch of codes to get started.</p>
  </div>

  <!-- Table -->
  <div *ngIf="!loading && pagedCodes.length > 0" class="rounded-xl border border-gray-200 overflow-hidden">
    <table class="w-full text-sm">
      <thead>
        <tr class="bg-gray-50 border-b border-gray-200">
          <th class="px-4 py-3 w-8">
            <input
              type="checkbox"
              [checked]="allPageSelected"
              (change)="toggleSelectAll()"
              class="w-4 h-4 rounded accent-primary cursor-pointer"
              title="Select all active on this page"
            />
          </th>
          <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">#</th>
          <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Code</th>
          <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400 hidden sm:table-cell">Role</th>
          <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400 hidden md:table-cell">Form</th>
          <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400 hidden lg:table-cell">Expires</th>
          <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Status</th>
          <th class="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Actions</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-200">
        <tr
          *ngFor="let code of pagedCodes; let i = index"
          class="bg-white hover:bg-gray-50 transition-colors"
          [class.bg-primary-5]="isSelected(code.id)"
        >
          <td class="px-4 py-3">
            <input
              *ngIf="canRevoke(code)"
              type="checkbox"
              [checked]="isSelected(code.id)"
              (change)="toggleSelect(code.id)"
              class="w-4 h-4 rounded accent-primary cursor-pointer"
            />
            <span *ngIf="!canRevoke(code)" class="w-4 h-4 inline-block"></span>
          </td>

          <td class="px-4 py-3 text-gray-400 text-xs">{{ page * size + i + 1 }}</td>

          <!-- Code -->
          <td class="px-4 py-3">
            <span class="font-mono text-xs font-semibold text-gray-800 bg-gray-100 px-2 py-1 rounded-lg border border-gray-200 select-all cursor-text">
              {{ code.code }}
            </span>
          </td>

          <!-- Role -->
          <td class="px-4 py-3 hidden sm:table-cell">
            <span class="inline-block px-2 py-0.5 rounded-md bg-gray-100 border border-gray-200 text-xs font-semibold text-gray-600">
              {{ code.roleName || getRoleById(code.roleId)?.name || '—' }}
            </span>
          </td>

          <!-- Form -->
          <td class="px-4 py-3 hidden md:table-cell">
            <span class="text-sm text-gray-600">{{ code.formName || getFormById(code.formId)?.name || '—' }}</span>
          </td>

          <!-- Expires -->
          <td class="px-4 py-3 hidden lg:table-cell">
            <span class="text-sm" [class.text-red-500]="isExpired(code.expiresAt)" [class.text-gray-600]="!isExpired(code.expiresAt)">
              {{ formatDate(code.expiresAt) }}
            </span>
          </td>

          <!-- Status -->
          <td class="px-4 py-3">
            <span
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border"
              [ngClass]="{
                'bg-green-50 text-green-700 border-green-200': getStatusLabel(code) === 'Active',
                'bg-blue-50 text-blue-700 border-blue-200': getStatusLabel(code) === 'Used',
                'bg-orange-50 text-orange-700 border-orange-200': getStatusLabel(code) === 'Expired',
                'bg-red-50 text-red-600 border-red-200': getStatusLabel(code) === 'Revoked'
              }"
            >
              <span
                class="w-1.5 h-1.5 rounded-full inline-block"
                [ngClass]="{
                  'bg-green-500': getStatusLabel(code) === 'Active',
                  'bg-blue-500': getStatusLabel(code) === 'Used',
                  'bg-orange-500': getStatusLabel(code) === 'Expired',
                  'bg-red-500': getStatusLabel(code) === 'Revoked'
                }"
              ></span>
              {{ getStatusLabel(code) }}
            </span>
          </td>

          <!-- Actions -->
          <td class="px-4 py-3" (click)="$event.stopPropagation()">
            <div class="flex items-center justify-end gap-1">
              <button
                *ngIf="canRevoke(code)"
                (click)="openRevokeModal(code)"
                title="Revoke code"
                class="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 cursor-pointer transition-colors"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"/>
                </svg>
              </button>
              <span *ngIf="!canRevoke(code)" class="px-2 text-xs text-gray-300 italic">—</span>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Pagination -->
  <div *ngIf="filteredCodes.length > size" class="flex items-center justify-between pt-2">
    <span class="text-xs text-gray-400">
      Showing {{ getMinValue(page * size + size, filteredCodes.length) }} of {{ filteredCodes.length }}
    </span>
    <div class="flex items-center gap-1">
      <button
        (click)="prevPage()"
        [disabled]="!hasPrev"
        class="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 font-medium cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
        </svg>
        Prev
      </button>
      <span class="px-3 py-1.5 text-sm text-gray-600">{{ page + 1 }} / {{ totalPages }}</span>
      <button
        (click)="nextPage()"
        [disabled]="!hasNext"
        class="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 font-medium cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Next
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
        </svg>
      </button>
    </div>
  </div>

</div>


<!-- ══════════════════════════════════════ GENERATE MODAL -->
<div *ngIf="showGenerateModal" class="fixed inset-0 z-50 flex items-center justify-center p-4" (click)="closeGenerateModal()">
  <div class="absolute inset-0 bg-black/40"></div>
  <div class="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5" (click)="$event.stopPropagation()">

    <div class="flex items-center justify-between">
      <h3 class="text-lg font-bold text-gray-800">Generate Activation Codes</h3>
      <button (click)="closeGenerateModal()" [disabled]="generateLoading" class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors disabled:opacity-40">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      </button>
    </div>

    <div *ngIf="generateError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ generateError }}
    </div>

    <div class="space-y-4">
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Role *</label>
        <select
          [(ngModel)]="generateForm.roleId"
          class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors cursor-pointer"
        >
          <option value="">Select role...</option>
          <option *ngFor="let role of allRoles" [value]="role.id">{{ role.name }}</option>
        </select>
      </div>

      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Form *</label>
        <select
          [(ngModel)]="generateForm.formId"
          class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors cursor-pointer"
        >
          <option value="">Select form...</option>
          <option *ngFor="let form of allForms" [value]="form.id">{{ form.name }}</option>
        </select>
      </div>

      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Number of Codes *</label>
        <input
          type="number"
          [(ngModel)]="generateForm.count"
          min="1"
          max="500"
          placeholder="e.g. 30"
          class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-primary transition-colors"
        />
        <p class="text-xs text-gray-400 px-1">Maximum 500 codes per batch</p>
      </div>

      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Expiry Date *</label>
        <input
          type="date"
          [(ngModel)]="generateForm.expiresAt"
          [min]="minExpiryDate"
          class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors cursor-pointer"
        />
      </div>
    </div>

    <div class="flex items-center justify-end gap-3 pt-1">
      <button (click)="closeGenerateModal()" [disabled]="generateLoading" class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
        Cancel
      </button>
      <button (click)="submitGenerate()" [disabled]="generateLoading" class="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
        <svg *ngIf="generateLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
        {{ generateLoading ? 'Generating...' : 'Generate Batch' }}
      </button>
    </div>

  </div>
</div>


<!-- ══════════════════════════════════════ REVOKE SINGLE MODAL -->
<div *ngIf="showRevokeModal" class="fixed inset-0 z-50 flex items-center justify-center p-4" (click)="closeRevokeModal()">
  <div class="absolute inset-0 bg-black/40"></div>
  <div class="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5" (click)="$event.stopPropagation()">

    <div class="flex items-start gap-4">
      <div class="w-10 h-10 rounded-xl bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0">
        <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
        </svg>
      </div>
      <div>
        <h3 class="text-base font-bold text-gray-800">Revoke Code</h3>
        <p class="text-sm text-gray-600 mt-1">
          Are you sure you want to revoke
          <span class="font-mono font-semibold text-gray-800">{{ selectedCode?.code }}</span>?
          This cannot be undone.
        </p>
      </div>
    </div>

    <div *ngIf="revokeError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ revokeError }}
    </div>

    <div class="flex items-center justify-end gap-3">
      <button (click)="closeRevokeModal()" [disabled]="revokeLoading" class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
        Cancel
      </button>
      <button (click)="confirmRevoke()" [disabled]="revokeLoading" class="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
        <svg *ngIf="revokeLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
        {{ revokeLoading ? 'Revoking...' : 'Revoke Code' }}
      </button>
    </div>

  </div>
</div>


<!-- ══════════════════════════════════════ BULK REVOKE MODAL -->
<div *ngIf="showBulkRevokeModal" class="fixed inset-0 z-50 flex items-center justify-center p-4" (click)="closeBulkRevokeModal()">
  <div class="absolute inset-0 bg-black/40"></div>
  <div class="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5" (click)="$event.stopPropagation()">

    <div class="flex items-start gap-4">
      <div class="w-10 h-10 rounded-xl bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0">
        <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
        </svg>
      </div>
      <div>
        <h3 class="text-base font-bold text-gray-800">Bulk Revoke</h3>
        <p class="text-sm text-gray-600 mt-1">
          You are about to revoke <span class="font-semibold text-gray-800">{{ selectedCount }} activation code{{ selectedCount === 1 ? '' : 's' }}</span>.
          This action cannot be undone.
        </p>
      </div>
    </div>

    <div *ngIf="bulkRevokeError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ bulkRevokeError }}
    </div>

    <div class="flex items-center justify-end gap-3">
      <button (click)="closeBulkRevokeModal()" [disabled]="bulkRevokeLoading" class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
        Cancel
      </button>
      <button (click)="confirmBulkRevoke()" [disabled]="bulkRevokeLoading" class="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
        <svg *ngIf="bulkRevokeLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
        {{ bulkRevokeLoading ? 'Revoking...' : 'Revoke ' + selectedCount + ' Code' + (selectedCount === 1 ? '' : 's') }}
      </button>
    </div>

  </div>
</div>